import React, { useEffect, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  Btn,
  CustomCalendar,
  CustomDropdown,
  CustomMultiSelect,
  CustomPanel,
  PageHeader,
} from '../../../../components'
import { Fieldset } from 'primereact/fieldset'
import { FaEye } from 'react-icons/fa'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import { errorHandler } from '../../../../services/util/error.service'
import {
  getIdsEntregasCanhoto,
  getRelacaoEntregasCanhoto,
  getResumoEntregasCanhoto,
  postDownloadCanhotosGestao,
} from '../../../../services/api/https/deliveries/delivery.http'
import { parseToMysqlFormat } from '../../../../services/util/date.service'
import { getEmpresas, getUsuarioInfo } from '../../../../services/localstorage/localstorage.service'
import CustomModal from '../../../../components/CustomModal/CustomModal'
import { useGestaoCanhotoContext } from '../../../../contexts/EntregaContext'
import CanhotoCards from './CanhotoCards'
import EntregasCanhotoDataTable from './EntregasCanhotoDataTable'

const TEM_CANHOTO = 1

const STATUS_COM_CANHOTO_OPTIONS = [
  { label: 'Entregue', value: 'Entregue' },
  { label: 'Devolução Parcial', value: 'Devolução Parcial' },
]

const mysqlToJsDate = value => {
  const [year, month, day] = value.split('-')
  return new Date(year, month - 1, day)
}

function EntregaComCanhoto({ history, dispatch, selectedTp }) {
  // Vars
  const {
    location: {
      state: { transportadora_id, dataInicial, dataFinal, transportadora, company_id },
    },
  } = history
  const context = useGestaoCanhotoContext()
  const empresasOptions = getEmpresas()
  // Estado guardado ao abrir um canhoto, para não refazer a busca ao voltar
  const cache = context.gestaoCanhotoContext.comCanhoto
  const restaurar = !!(cache && cache.restaurarAoVoltar)

  // Hooks
  const [empresa, setEmpresa] = useState(
    restaurar ? cache.empresa : empresasOptions.find(e => e.id === company_id) || null,
  )
  const [pInicial, setPeriodoInicial] = useState(
    restaurar ? cache.pInicial : mysqlToJsDate(dataInicial),
  )
  const [pFinal, setPeriodoFinal] = useState(restaurar ? cache.pFinal : mysqlToJsDate(dataFinal))
  const [filtrosAtivos, setFiltrosAtivos] = useState(restaurar ? cache.filtrosAtivos : null)
  const [resumo, setResumo] = useState(restaurar ? cache.resumo : null)
  const [entregas, setEntregas] = useState(restaurar ? cache.entregas : [])
  const [totalRecords, setTotalRecords] = useState(restaurar ? cache.totalRecords : 0)
  const [first, setFirst] = useState(restaurar ? cache.first : 0)
  const [rows, setRows] = useState(restaurar ? cache.rows : 20)
  const [loadingTable, setLoadingTable] = useState(false)
  const [statusSelecionados, setStatusSelecionados] = useState(
    restaurar ? cache.statusSelecionados : [],
  )
  const [search, setSearch] = useState(restaurar ? cache.search : '')
  const [selectedIds, setSelectedIds] = useState(restaurar ? cache.selectedIds : [])
  const [downloadInfo, setDownloadInfo] = useState(null)
  const searchTimer = useRef(null)

  // Helpers
  const montarFiltros = (empresa, pInicial, pFinal) => ({
    dataInicial: parseToMysqlFormat(pInicial),
    dataFinal: parseToMysqlFormat(pFinal),
    transportadoraId: transportadora_id || '',
    companyId: empresa.id,
  })

  const fetchPagina = async (filtros, { first, rows, statusSel, searchTerm }) => {
    try {
      setLoadingTable(true)
      const retorno = await getRelacaoEntregasCanhoto(selectedTp, {
        ...filtros,
        temCanhoto: TEM_CANHOTO,
        status: statusSel.join(','),
        search: searchTerm,
        page: Math.floor(first / rows),
        pageSize: rows,
      })
      setEntregas(retorno.rows)
      setTotalRecords(retorno.total)
      setFirst(first)
      setRows(rows)
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      setLoadingTable(false)
    }
  }

  const fetchEntregas = async (empresa, pInicial, pFinal) => {
    try {
      if (selectedTp === 0) throw new Error('Selecione o Operador Logístico')
      if (!empresa) throw new Error('Selecione a Empresa')
      dispatch(showLoadingBox('Carregando as entregas com canhoto', 'Aguarde . . .'))

      const filtros = montarFiltros(empresa, pInicial, pFinal)
      const [dadosResumo] = await Promise.all([
        getResumoEntregasCanhoto(selectedTp, filtros),
        fetchPagina(filtros, { first: 0, rows, statusSel: [], searchTerm: '' }),
      ])

      setResumo(dadosResumo)
      setStatusSelecionados([])
      setSearch('')
      setSelectedIds([])
      setFiltrosAtivos(filtros)
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      dispatch(hideLoadingBox())
    }
  }

  useEffect(() => {
    if (restaurar) {
      context.setGestaoCanhotoContext(prev => ({ ...prev, comCanhoto: null }))
    } else if (empresa) {
      fetchEntregas(empresa, pInicial, pFinal)
    }
  }, [])

  const onPeriodoChange = (campo, value) => {
    context.setGestaoCanhotoContext(prev => ({
      ...prev,
      selectedPeriodo: { ...prev.selectedPeriodo, [campo]: value },
    }))
    if (campo === 'dataInicial') setPeriodoInicial(value)
    else setPeriodoFinal(value)
  }

  const onStatusChange = value => {
    setStatusSelecionados(value)
    if (filtrosAtivos) {
      fetchPagina(filtrosAtivos, { first: 0, rows, statusSel: value, searchTerm: search })
    }
  }

  const onSearchChange = value => {
    setSearch(value)
    if (!filtrosAtivos) return
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      fetchPagina(filtrosAtivos, {
        first: 0,
        rows,
        statusSel: statusSelecionados,
        searchTerm: value,
      })
    }, 600)
  }

  const onPage = e => {
    fetchPagina(filtrosAtivos, {
      first: e.first,
      rows: e.rows,
      statusSel: statusSelecionados,
      searchTerm: search,
    })
  }

  const onToggleAll = async marcar => {
    if (!marcar) {
      setSelectedIds([])
      return
    }
    try {
      setLoadingTable(true)
      const ids = await getIdsEntregasCanhoto(selectedTp, {
        ...filtrosAtivos,
        temCanhoto: TEM_CANHOTO,
        status: statusSelecionados.join(','),
        search,
      })
      setSelectedIds(ids)
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      setLoadingTable(false)
    }
  }

  const onClickVisualizar = deliveryId => {
    context.setGestaoCanhotoContext(prev => ({
      ...prev,
      comCanhoto: {
        restaurarAoVoltar: true,
        empresa,
        pInicial,
        pFinal,
        filtrosAtivos,
        resumo,
        entregas,
        totalRecords,
        first,
        rows,
        statusSelecionados,
        search,
        selectedIds,
      },
    }))
    history.push('/main/entregas/anexar-canhoto', { delivery_id: deliveryId })
  }

  const onClickDownloadCanhotos = async () => {
    try {
      dispatch(showLoadingBox('Solicitando o download dos canhotos', 'Aguarde . . .'))
      const retorno = await postDownloadCanhotosGestao(selectedTp, {
        delivery_ids: selectedIds,
        user_id: getUsuarioInfo().id,
        transportadora,
        empresa: empresa.name,
        dataInicial: parseToMysqlFormat(pInicial),
        dataFinal: parseToMysqlFormat(pFinal),
      })
      setDownloadInfo(retorno)
      setSelectedIds([])
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      dispatch(hideLoadingBox())
    }
  }

  // Render
  return (
    <React.Fragment>
      <PageHeader>
        3.17 - Gestão de Canhotos – Entregas com Canhoto –<strong> {transportadora}</strong>
      </PageHeader>

      <div style={{ marginTop: 20 }}>
        <CustomPanel showBtnNovo={false} onVoltar={history.goBack}>
          <div className="p-grid">
            <div className="p-col-3">
              <CustomDropdown
                label="Empresa"
                optionLabel="name"
                options={empresasOptions}
                value={empresa}
                onChange={setEmpresa}
                placeholder="Selecione a empresa"
                filter={true}
              />
            </div>
            <div className="p-col-9" style={{ display: 'flex' }}>
              <CustomCalendar
                label="Período Inicial"
                value={pInicial}
                onChange={value => onPeriodoChange('dataInicial', value)}
              />
              <CustomCalendar
                label="Período Final"
                value={pFinal}
                onChange={value => onPeriodoChange('dataFinal', value)}
              />
              <div style={{ marginLeft: 10, paddingTop: 27 }}>
                <Btn onClick={() => fetchEntregas(empresa, pInicial, pFinal)}>Buscar</Btn>
              </div>
            </div>
          </div>

          <CanhotoCards resumo={resumo} />

          <Fieldset legend="Filtro" toggleable collapsed={true} className="p-col-12">
            <div className="p-grid p-align-end">
              <div className="p-col-3">
                <CustomMultiSelect
                  label="Status"
                  options={STATUS_COM_CANHOTO_OPTIONS}
                  value={statusSelecionados}
                  onChange={onStatusChange}
                  filter={false}
                />
              </div>
            </div>
          </Fieldset>

          <div className="p-grid" style={{ marginTop: 15 }}>
            <div className="p-col-9" style={{ display: 'flex' }}>
              <Btn onClick={onClickDownloadCanhotos} disabled={selectedIds.length === 0}>
                Download Canhotos
              </Btn>
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <EntregasCanhotoDataTable
              entregas={entregas}
              totalRecords={totalRecords}
              first={first}
              rows={rows}
              loading={loadingTable}
              onPage={onPage}
              search={search}
              onSearchChange={onSearchChange}
              acaoIcon={<FaEye />}
              onClickAcao={onClickVisualizar}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onToggleAll={onToggleAll}
            />
          </div>
        </CustomPanel>
      </div>

      <CustomModal
        header="Download de Canhotos"
        isVisible={!!downloadInfo}
        onClose={() => setDownloadInfo(null)}
        width={600}
      >
        {downloadInfo && (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <p style={{ fontSize: 16, marginBottom: 24 }}>
              A geração do arquivo com <b>{downloadInfo.total}</b> canhoto(s) já está em andamento!
              <br />O link para download será enviado ao email <b>{downloadInfo.email}</b> em alguns
              minutos (seleções com milhares de canhotos podem levar até 15 minutos).
              <br />
              <br />
              Caso não tenha recebido o email na caixa de entrada, verifique a área de spam.
              <br />
              <br />
              Atenção! O arquivo ficará disponível para download somente por <b>24 horas</b>, após o
              envio do email.
            </p>
            <Btn onClick={() => setDownloadInfo(null)}>OK</Btn>
          </div>
        )}
      </CustomModal>
    </React.Fragment>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(withRouter(EntregaComCanhoto))
